import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  getCompanyAgencyOnboardingPath,
  getUserRole,
  hasFullName,
  isPendingUserRoleSelection,
  requiresCompanyAgencyOnboarding,
  ROLE_IDS,
} from "../constants/roleConfig";

const getPath = (role, job_seeker_id) => {
  switch (role) {
    case 1:
      return "/dashboard";
    case 2:
      return job_seeker_id ? "/applicant-dashboard" : "/resume-builder";
    case 3:
      return "/user-role";
    case 4:
      return "/client-dashboard";
    case 5:
      return "/dashboard";
    default:
      return "/user-role";
  }
};

function CheckRoute({ component: Component }) {
  const authToken = useSelector((state) => state.auth.token);
  const role = useSelector(
    (state) => getUserRole(state.auth?.user)
  );
  const job_seeker_id = useSelector(
    (state) => state.auth.user?.job_seeker_id ?? state.auth?.jobseeker_id
  );
  const redirectUrl = useSelector((state) => state?.auth?.redirectUrl);
  // profile_completed lives on the user object, not at the top-level auth state.
  const profile_completed = useSelector(
    (state) => state?.auth?.user?.profile_completed
  );
  const groups = useSelector((state) => state?.auth?.user?.groups);
  const company_id = useSelector((state) => state?.auth?.user?.company_id);
  const full_name = useSelector((state) => state?.auth?.user?.full_name);
  const isNewUser = useSelector((state) => state?.auth?.is_new_user);

  // Client email link: only honour client-dashboard redirect for actual clients (role 4). Role 3 = User → never send to client-dashboard.
  const isClientRole = role === 4;
  if (authToken && redirectUrl && redirectUrl.includes("/client-dashboard") && isClientRole) {
    return <Navigate to={redirectUrl} replace />;
  }

  // Role 3 (User): is_new_user is always false from the API on repeat logins, even when
  // the user never finished role selection. profile_completed is the reliable gate.
  // Always send role 3 users to /user-role until profile_completed is explicitly true,
  // regardless of any redirectUrl.
  // IMPORTANT API CONTRACT: isPendingUserRoleSelection relies on job_seeker_id being
  // absent for role-3 users who haven't completed role selection. If the API ever sets
  // job_seeker_id on role-3 before actual profile completion, this guard will be bypassed
  // and the user will skip /user-role. Verify that the API does NOT set job_seeker_id
  // until the user has genuinely completed the role-selection + profile flow.
  if (authToken && isPendingUserRoleSelection(role, job_seeker_id)) {
    return <Navigate to="/user-role" replace />;
  }

  // Role 2 (jobseeker) must not be sent to the recruiter pricing/billing page
  // captured from a public pricing CTA. CheckRoute is the last gate guarding the
  // dashboard, so this guard is race-proof even if an upstream redirect clear has
  // not yet propagated. Other redirects (job page, invitation) are preserved.
  const isJobseekerPricingRedirect =
    role === 2 && redirectUrl && redirectUrl.includes("from=pricing");

  if (isNewUser) {
    if (authToken && profile_completed && redirectUrl && !isJobseekerPricingRedirect) {
      return <Navigate to={redirectUrl} replace />;
    }
  } else {
    // Existing user: don't send to onboarding even if redirectUrl is from invite link
    const isOnboardingRedirect =
      redirectUrl &&
      (redirectUrl.includes("/onboarding") || redirectUrl.includes("source=invitation"));
    if (authToken && redirectUrl && !isOnboardingRedirect && !isJobseekerPricingRedirect) {
      return <Navigate to={redirectUrl} replace />;
    }
  }
  if (authToken) {
    if (requiresCompanyAgencyOnboarding(role, profile_completed, groups, company_id)) {
      return (
        <Navigate to={getCompanyAgencyOnboardingPath(role)} replace />
      );
    }
    // Role 1/5 with no full_name: must complete name onboarding before dashboard
    if (
      (role === ROLE_IDS.RECRUITER || role === ROLE_IDS.AGENCY) &&
      !hasFullName(full_name)
    ) {
      return (
        <Navigate to={getCompanyAgencyOnboardingPath(role)} replace />
      );
    }
    return (
      <Navigate
        to={role ? getPath(role, job_seeker_id) : "/user-role"}
        replace
      />
    );
  }
  return <Component />;
}

export default CheckRoute;
